/**
 * Card peek (REDESIGN.md 3.5): long-press (touch, 350ms) or hover (pointer,
 * 500ms) on any face-up card shows it large, with its name, above the finger
 * or pointer. Any element with data-peek="<value>" opts in. A long-press
 * swallows the click that follows, so peeking never plays a card.
 */
import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { FlatCard } from './Card';

interface PeekState {
  value: number;
  x: number;
  y: number;
  below: boolean;
}

const W = 168;

export function usePeek(root: RefObject<HTMLElement | null>, enabled = true): PeekState | null {
  const [peek, setPeek] = useState<PeekState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const swallow = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = root.current;
    if (!el || !enabled) return;
    const show = (target: HTMLElement, mouse = false) => {
      if (!target.isConnected || (mouse && target.closest('.slot.target'))) return;
      const v = Number(target.dataset.peek);
      if (!Number.isFinite(v)) return;
      const r = target.getBoundingClientRect();
      // Already shown large: nothing to add.
      if (r.width >= 140) return;
      const h = W * 1.5;
      const below = r.top - h - 12 < 8;
      const x = Math.max(8, Math.min(window.innerWidth - W - 8, r.left + r.width / 2 - W / 2));
      const y = below ? Math.min(window.innerHeight - h - 8, r.bottom + 12) : r.top - h - 12;
      setPeek({ value: v, x, y, below });
    };
    const clear = () => {
      clearTimeout(timer.current);
      setPeek(null);
    };
    const peekTarget = (e: Event) => {
      const t = (e.target as HTMLElement | null)?.closest?.('[data-peek]') as HTMLElement | null;
      if (!t || !el.contains(t)) return null;
      // Hovering a card you are about to swap onto: the ghost card is enough.
      if (t.closest('.slot.target') && (e as PointerEvent).pointerType === 'mouse') return null;
      return t;
    };
    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') {
        clear();
        return;
      }
      const t = peekTarget(e);
      if (!t) return;
      start.current = { x: e.clientX, y: e.clientY };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        swallow.current = true;
        show(t);
      }, 350);
    };
    const move = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' || !start.current) return;
      if (Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 10) {
        start.current = null;
        clear();
      }
    };
    const up = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      start.current = null;
      clear();
    };
    const over = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const t = peekTarget(e);
      clearTimeout(timer.current);
      if (!t) {
        setPeek(null);
        return;
      }
      timer.current = setTimeout(() => show(t, true), 500);
    };
    const leave = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const to = (e.relatedTarget as HTMLElement | null)?.closest?.('[data-peek]');
      if (!to) clear();
    };
    const click = (e: MouseEvent) => {
      if (swallow.current) {
        swallow.current = false;
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const menu = (e: Event) => {
      if (peekTarget(e)) e.preventDefault();
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('pointerover', over);
    el.addEventListener('pointerout', leave);
    el.addEventListener('click', click, true);
    el.addEventListener('contextmenu', menu);
    return () => {
      clearTimeout(timer.current);
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('pointerover', over);
      el.removeEventListener('pointerout', leave);
      el.removeEventListener('click', click, true);
      el.removeEventListener('contextmenu', menu);
    };
  }, [root, enabled]);
  return peek;
}

export function PeekCard({ peek }: { peek: PeekState | null }) {
  if (!peek) return null;
  return (
    <div className="peek" style={{ left: peek.x, top: peek.y }} aria-hidden="true">
      <FlatCard value={peek.value} width={W} peek={false} />
    </div>
  );
}
